import React from 'react';
import { Box, Typography, Breadcrumbs, Link, Stack, Button } from '@mui/material';
import { NavigateNext, ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { COLORS } from '../../settings/themeSettings';

const PageHeader = ({ 
    title, 
    subtitle, 
    breadcrumbs = [], 
    actions = [], 
    icon: Icon,
    showBackButton = false
}) => {
    const navigate = useNavigate();

    return (
        <Box sx={{ mb: 4 }}>
            {/* Breadcrumbs */}
            {breadcrumbs.length > 0 && (
                <Breadcrumbs 
                    separator={<NavigateNext fontSize="small" />} 
                    aria-label="breadcrumb"
                    sx={{ mb: 1 }}
                >
                    {breadcrumbs.map((crumb, index) => (
                        index === breadcrumbs.length - 1 ? (
                            <Typography key={index} color="text.primary" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                                {crumb.label}
                            </Typography>
                        ) : (
                            <Link
                                key={index}
                                underline="hover"
                                color="inherit"
                                href={crumb.path}
                                onClick={(e) => {
                                    e.preventDefault();
                                    navigate(crumb.path);
                                }}
                                sx={{ fontSize: '0.85rem', color: 'text.secondary', '&:hover': { color: COLORS.primary } }}
                            >
                                {crumb.label}
                            </Link>
                        )
                    ))}
                </Breadcrumbs>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
                <Stack direction="row" spacing={2} alignItems="center">
                    {showBackButton && (
                        <Button 
                            variant="outlined" 
                            size="small" 
                            onClick={() => navigate(-1)}
                            sx={{ 
                                minWidth: 'auto', 
                                p: 1, 
                                borderRadius: '10px', 
                                borderColor: COLORS.gray[200],
                                color: COLORS.gray[600],
                                '&:hover': { borderColor: COLORS.primary, color: COLORS.primary, bgcolor: `${COLORS.primary}05` }
                            }}
                        >
                            <ArrowBack fontSize="small" />
                        </Button>
                    )}
                    
                    {Icon && (
                        <Box sx={{ 
                            p: 1.5, 
                            borderRadius: '12px', 
                            bgcolor: `${COLORS.primary}10`, 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center' 
                        }}>
                            <Icon sx={{ color: COLORS.primary, fontSize: 28 }} />
                        </Box>
                    )}
                    
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: COLORS.secondary, letterSpacing: '-0.02em' }}>
                            {title}
                        </Typography>
                        {subtitle && (
                            <Typography variant="body2" sx={{ color: COLORS.text.secondary, mt: 0.5 }}>
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                </Stack>

                <Stack direction="row" spacing={1.5}>
                    {actions.map((action, index) => (
                        <Button
                            key={index}
                            variant={action.variant || "contained"}
                            color={action.color || "primary"}
                            startIcon={action.icon}
                            onClick={action.onClick}
                            sx={{ 
                                textTransform: 'none', 
                                fontWeight: 600, 
                                borderRadius: '10px',
                                px: 2.5,
                                py: 1,
                                boxShadow: action.variant === 'contained' ? `0 4px 12px ${COLORS.primary}30` : 'none'
                            }}
                        >
                            {action.label}
                        </Button>
                    ))}
                </Stack>
            </Stack>
        </Box>
    );
};

export default PageHeader;
